import { useInfiniteQuery } from 'react-query';

import {
  baseApiUrl,
  fetcher,
  ServerErrorResponse,
  createParamString,
  infiniteEmptyResults,
} from 'data_sources/constants';

import type { ManualVersion } from 'views/App/Portal/Policy/_types/manuals/draft';
import type { ApiCollectionResponse } from 'types/api_response';

export default function useArchivedManualVersions<T extends ManualVersion>(
  organizationId: string,
  manualId: string,
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexipub/manuals/archived/v1/manuals/${manualId}`;

  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['archived_manual_versions', organizationId, manualId, params],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        const paramString = createParamString(params);
        const fullUrl = pageParam || `${url}?${paramString}`;

        const data: ApiCollectionResponse<T> = await fetcher({
          url: fullUrl,
          method: 'get',
          options: { signal },
        });

        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            offset: lastPage.offset,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}
